import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { deleteModelFile, getModelFilePreview } from '@/api/modelFiles';
import { deleteProgramFile } from '@/api/programFiles';
import {
  cancelCollisionDetection,
  createProject,
  deleteProject,
  getProject,
  getSimulationMesh,
  getSimulationVideo,
  type ListProjectsParams,
  listProjects,
  moveProgramFile,
  startCollisionDetection,
  type UpdateProjectParams,
  type UploadModelFileParams,
  updateProject,
  uploadModelFile,
  uploadProgramFiles,
} from '@/api/projects';
import { queryKeys } from '@/api/queryKeys';
import { useTaskProgressContext } from '@/api/TaskProgressProvider';
import type { DrillSide, ProgramFileInfo, ProjectDetail, QueueTaskInfo } from '@/types/api';

// Runs mutationFn and writes the returned ProjectDetail directly into the cache.
const useProjectDetailMutation = <TVars>(projectId: string, mutationFn: (vars: TVars) => Promise<ProjectDetail>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (updated) => queryClient.setQueryData(queryKeys.projectDetail(projectId), updated),
  });
};

// Runs mutationFn and invalidates the project detail cache entry on success.
const useInvalidatingProjectMutation = <TVars>(projectId: string, mutationFn: (vars: TVars) => Promise<void>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.projectDetail(projectId) }),
  });
};

export const useProjectList = (params: ListProjectsParams) =>
  useQuery({
    queryKey: queryKeys.projectList(params),
    queryFn: () => listProjects(params),
  });

export const useProjectDetail = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.projectDetail(id ?? ''),
    queryFn: () => getProject(id!),
    enabled: !!id,
    refetchInterval: (query) => (query.state.data?.simulation_state === 'running' ? 5_000 : false),
  });

export const useSimulationVideo = (projectId: string) =>
  useQuery({
    queryKey: queryKeys.simulationVideo(projectId),
    queryFn: () => getSimulationVideo(projectId),
    staleTime: Number.POSITIVE_INFINITY,
  });

export const useSimulationMesh = (projectId: string) =>
  useQuery({
    queryKey: queryKeys.simulationMesh(projectId),
    queryFn: () => getSimulationMesh(projectId),
    staleTime: Number.POSITIVE_INFINITY,
  });

export const useModelPreview = (modelId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.modelPreview(modelId ?? ''),
    queryFn: () => getModelFilePreview(modelId!),
    enabled: !!modelId,
    staleTime: Number.POSITIVE_INFINITY,
  });

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createProject,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects', 'list'] }),
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => deleteProject(projectId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  });
};

export const useUpdateProject = (id: string) =>
  useProjectDetailMutation(id, (p: UpdateProjectParams) => updateProject(id, p));

export const useUploadModelFile = (id: string) =>
  useProjectDetailMutation(id, (p: UploadModelFileParams) => uploadModelFile(id, p));

export const useUploadProgramFiles = (id: string) =>
  useProjectDetailMutation(id, ({ files, drillSide }: { files: File[]; drillSide: DrillSide }) =>
    uploadProgramFiles(id, files, drillSide)
  );

export const useStartCollisionDetection = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => startCollisionDetection(id),
    onSuccess: ({ project }) => queryClient.setQueryData(queryKeys.projectDetail(id), project),
  });
};

export const useCancelCollisionDetection = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => cancelCollisionDetection(id),
    onSuccess: (project) => queryClient.setQueryData(queryKeys.projectDetail(id), project),
  });
};

export const useTaskProgress = (backgroundTaskId: string | null): QueueTaskInfo | null => {
  const { subscribe, unsubscribe } = useTaskProgressContext();
  const [progress, setProgress] = useState<QueueTaskInfo | null>(null);

  useEffect(() => {
    if (!backgroundTaskId) return;

    subscribe(backgroundTaskId, setProgress);
    return () => {
      unsubscribe(backgroundTaskId, setProgress);
    };
  }, [backgroundTaskId, subscribe, unsubscribe]);

  if (!backgroundTaskId) return null;
  return progress;
};

export const useDeleteModelFile = (id: string) =>
  useInvalidatingProjectMutation(id, (modelFileId: string) => deleteModelFile(modelFileId));

export const useDeleteProgramFile = (id: string) =>
  useInvalidatingProjectMutation(id, (programFileId: string) => deleteProgramFile(programFileId));

interface MoveProgramFileVars {
  fileId: string;
  afterId: string | null;
  optimisticFiles: ProgramFileInfo[];
}

export const useMoveProgramFile = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation<ProjectDetail, Error, MoveProgramFileVars, { snapshot: ProjectDetail | undefined }>({
    mutationFn: ({ fileId, afterId }) => moveProgramFile(projectId, fileId, afterId),
    onMutate: async ({ optimisticFiles }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.projectDetail(projectId) });
      const snapshot = queryClient.getQueryData<ProjectDetail>(queryKeys.projectDetail(projectId));
      queryClient.setQueryData<ProjectDetail>(queryKeys.projectDetail(projectId), (prev) =>
        // biome-ignore lint/style/useNamingConvention: API uses snake_case
        prev ? { ...prev, program_files: optimisticFiles } : prev
      );
      return { snapshot };
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.projectDetail(projectId), updated);
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshot) {
        queryClient.setQueryData(queryKeys.projectDetail(projectId), context.snapshot);
      }
    },
  });
};
